/**
 * Our Locations Section frontend: independent Splide sliders per location card.
 */
import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';

( function () {
	'use strict';

	const DEFAULT_INTERVAL = 4000;
	const DEFAULT_SPEED = 800;
	const sliderInstances = [];

	/**
	 * Read the responsive slider height from CSS custom properties.
	 *
	 * @param {HTMLElement} sliderRoot Slider root element.
	 * @return {string} CSS height value.
	 */
	function getSliderHeight( sliderRoot ) {
		const section = sliderRoot.closest( '.alteia-our-locations-section' );

		if ( ! section ) {
			return '350px';
		}

		const height = getComputedStyle( section )
			.getPropertyValue( '--alteia-our-locations-section-slider-height' )
			.trim();

		return height || '350px';
	}

	/**
	 * Remove Splide UI we replace with custom controls.
	 *
	 * @param {HTMLElement} root Slider root element.
	 */
	function cleanupSplideChrome( root ) {
		root.querySelectorAll( '.splide__pagination, .splide__arrows' ).forEach(
			( element ) => element.remove()
		);

		root.querySelectorAll( '.splide__sr' ).forEach( ( element ) => {
			element.setAttribute( 'aria-hidden', 'true' );
		} );
	}

	/**
	 * Resolve the real slide index from Splide.
	 *
	 * @param {import('@splidejs/splide').Splide} splide Splide instance.
	 * @param {number|undefined}                  dest   Destination index.
	 * @return {number} Active slide index.
	 */
	function resolveSlideIndex( splide, dest ) {
		if ( typeof dest === 'number' && ! Number.isNaN( dest ) ) {
			return dest;
		}

		return splide.index;
	}

	/**
	 * Update custom pagination dots for a card.
	 *
	 * @param {HTMLElement} card        Location card element.
	 * @param {number}      activeIndex Active slide index.
	 */
	function updatePagination( card, activeIndex ) {
		card.querySelectorAll( '[data-locations-dot]' ).forEach( ( dot ) => {
			const dotIndex = parseInt(
				dot.getAttribute( 'data-locations-dot' ),
				10
			);
			const isActive = dotIndex === activeIndex;

			dot.classList.toggle(
				'alteia-our-locations-section__pagination-dot--active',
				isActive
			);
			dot.setAttribute( 'aria-current', isActive ? 'true' : 'false' );
		} );
	}

	/**
	 * Preload slider images to avoid flicker during transitions.
	 *
	 * @param {HTMLElement} card Location card element.
	 * @return {Promise<void>} Resolves when images are ready.
	 */
	function preloadCardImages( card ) {
		const images = card.querySelectorAll(
			'.alteia-our-locations-section__slide-image'
		);

		return Promise.all(
			Array.from( images ).map(
				( image ) =>
					new Promise( ( resolve ) => {
						if ( image.complete ) {
							resolve();
							return;
						}

						const done = () => resolve();

						image.addEventListener( 'load', done, { once: true } );
						image.addEventListener( 'error', done, { once: true } );

						window.setTimeout( done, 2500 );
					} )
			)
		);
	}

	/**
	 * Initialize one location card slider.
	 *
	 * @param {HTMLElement} card     Location card element.
	 * @param {Object}      settings Slider settings from section dataset.
	 */
	function initLocationCardSlider( card, settings ) {
		const sliderRoot = card.querySelector( '[data-locations-slider]' );

		if ( ! sliderRoot ) {
			return;
		}

		const slideCount =
			sliderRoot.querySelectorAll(
				'.splide__slide:not(.splide__slide--clone)'
			).length || sliderRoot.querySelectorAll( '.splide__slide' ).length;

		if ( slideCount <= 0 ) {
			return;
		}

		if ( slideCount <= 1 ) {
			const pagination = card.querySelector(
				'[data-locations-pagination]'
			);
			const controls = card.querySelector( '[data-locations-controls]' );

			if ( pagination ) {
				pagination.hidden = true;
			}

			if ( controls ) {
				controls.hidden = true;
			}

			return;
		}

		let splide = null;

		const splideOptions = {
			type: settings.loop ? 'loop' : 'slide',
			direction:
				document.documentElement.getAttribute( 'dir' ) === 'rtl'
					? 'rtl'
					: 'ltr',
			perPage: 1,
			perMove: 1,
			gap: 0,
			padding: 0,
			pagination: false,
			arrows: false,
			drag: slideCount > 1,
			speed: settings.speed,
			easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
			rewind: ! settings.loop,
			autoHeight: false,
			height: getSliderHeight( sliderRoot ),
			waitForTransition: true,
			flickPower: 300,
			flickMaxPages: 1,
			autoplay: settings.autoplay,
			interval: settings.interval,
			pauseOnHover: true,
			pauseOnFocus: true,
			resetProgress: false,
			reduceMotion: {
				speed: 0,
				rewindSpeed: 0,
				autoplay: false,
			},
		};

		const mountSlider = () => {
			splide = new Splide( sliderRoot, splideOptions );

			const syncPagination = ( dest ) => {
				updatePagination( card, resolveSlideIndex( splide, dest ) );
			};

			splide.on( 'mounted', () => {
				cleanupSplideChrome( sliderRoot );
				syncPagination();
				card.classList.add( 'is-locations-slider-ready' );
			} );

			splide.on( 'move', ( index, prev, dest ) => {
				syncPagination( dest );
			} );

			splide.mount();
			cleanupSplideChrome( sliderRoot );

			sliderInstances.push( { splide, sliderRoot } );
		};

		preloadCardImages( card ).then( mountSlider );

		if ( ! settings.arrows ) {
			const controls = card.querySelector( '[data-locations-controls]' );

			if ( controls ) {
				controls.hidden = true;
			}
		}

		if ( ! settings.pagination ) {
			const pagination = card.querySelector(
				'[data-locations-pagination]'
			);

			if ( pagination ) {
				pagination.hidden = true;
			}
		}

		card.addEventListener( 'click', ( event ) => {
			if ( ! splide ) {
				return;
			}

			if ( event.target.closest( '[data-locations-prev]' ) ) {
				event.preventDefault();
				splide.go( '<' );
				return;
			}

			if ( event.target.closest( '[data-locations-next]' ) ) {
				event.preventDefault();
				splide.go( '>' );
				return;
			}

			const dotButton = event.target.closest( '[data-locations-dot]' );

			if ( dotButton ) {
				event.preventDefault();
				const targetIndex = parseInt(
					dotButton.getAttribute( 'data-locations-dot' ),
					10
				);

				if ( ! Number.isNaN( targetIndex ) ) {
					splide.go( targetIndex );
				}
			}
		} );
	}

	/**
	 * Initialize one Our Locations section instance.
	 *
	 * @param {HTMLElement} section Section root element.
	 */
	function initLocationsSection( section ) {
		const settings = {
			autoplay: section.dataset.locationsAutoplay === 'true',
			interval:
				parseInt( section.dataset.locationsInterval, 10 ) ||
				DEFAULT_INTERVAL,
			speed:
				parseInt( section.dataset.locationsSpeed, 10 ) || DEFAULT_SPEED,
			loop: section.dataset.locationsLoop !== 'false',
			arrows: section.dataset.locationsArrows !== 'false',
			pagination: section.dataset.locationsPagination !== 'false',
		};

		section
			.querySelectorAll( '[data-locations-card]' )
			.forEach( ( card ) => initLocationCardSlider( card, settings ) );
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document
			.querySelectorAll( '.alteia-our-locations-section' )
			.forEach( initLocationsSection );

		let resizeTimer;

		window.addEventListener( 'resize', () => {
			window.clearTimeout( resizeTimer );
			resizeTimer = window.setTimeout( () => {
				sliderInstances.forEach( ( { splide, sliderRoot } ) => {
					const nextHeight = getSliderHeight( sliderRoot );

					if ( splide.options.height !== nextHeight ) {
						splide.options.height = nextHeight;
						splide.refresh();
					}
				} );
			}, 150 );
		} );
	} );
} )();
